import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, CircleDollarSignIcon, InfoIcon, LayersIcon, LockKeyholeIcon, Settings2Icon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { GeographySelector } from './GeographySelector';
import { PromotionSettings } from './PromotionSettings';
import { useNisoloSubmissions } from '../hooks/useNisoloSubmissions';
import { eddyIncomingBatches, onePeopleIncomingBatches, submissionId } from '../data/inventorySubmissions';
import { shushMark } from '../data/recoveryRules';
type Channel = {
  id: string;
  name: string;
  iconUrl?: string;
};
const channels: Channel[] = [{
  id: 'manymoons',
  name: 'Manymoons',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/1H17upTEPhdYPo8CxCujBt/M_manymoons.svg'
}, {
  id: 'prive',
  name: 'Privé',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/fHmVmW1X2zeRckQHVqfLBY/prive.svg'
}, {
  id: 'tiktok',
  name: 'Live Selling',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/1H17upTEPhdYPo8CxCujBt/M_manymoons.svg'
}, {
  id: 'whatnot',
  name: 'Discreet',
  iconUrl: shushMark
}, {
  id: 'ebay',
  name: 'eBay',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/7ctVShw3HBQcunanqiLN75/ebay.svg'
}, {
  id: 'poshmark',
  name: 'Poshmark',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/n5jkPZQbazKpxu5cMnVHR4/poshmark.svg'
}, {
  id: 'vinted',
  name: 'Vinted',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/6n8bfmq2ixHXbjbeTaLCJz/vinted.svg'
}, {
  id: 'vestiaire',
  name: 'Vestiaire',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/tLawC51vDdyw6q2RLyZ5Bc/vestiaire.svg'
}, {
  id: 'depop',
  name: 'Depop',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/fG9XmMcsDQikNiC1uba9MJ/dopop.svg'
}, {
  id: 'mercari',
  name: 'Mercari',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/cisCD3tYpjAhP5v7GoDZxD/mercari.svg'
}];
type SubmissionSettings = {
  channels: string[];
  indexPrice: number;
  indexAgreement: number;
  indexLocked: boolean;
};
const defaultSettings: SubmissionSettings = {
  channels: ['manymoons', 'ebay', 'whatnot'],
  indexPrice: 50,
  indexAgreement: 50,
  indexLocked: false
};
const brandSettingOverrides: Record<string, Partial<SubmissionSettings>> = {
  Nisolo: {
    indexPrice: 70,
    channels: ['manymoons', 'whatnot']
  }
};

// Individual uploads can depart from their brand's footprint — damaged goods,
// for example, only move through live selling and discreet channels.
const submissionSettingOverrides: Record<string, Partial<SubmissionSettings>> = {
  '472 — Imperfects': {
    channels: ['manymoons', 'prive', 'tiktok', 'whatnot']
  },
  '473 — Damages': {
    channels: ['tiktok', 'whatnot']
  }
};
export function ControlsDashboard({
  selectedOwner = '',
  initialScopeId = null,
  isAdmin = false
}: {
  selectedOwner?: string;
  initialScopeId?: string | null;
  isAdmin?: boolean;
}) {
  const allBrands = selectedOwner === '';
  const includes = (brand: string) => allBrands || selectedOwner === brand;
  const {
    data: nisoloSubmissions
  } = useNisoloSubmissions(includes('Nisolo'));
  const submissions = useMemo(() => {
    const rows: {
      id: string;
      brand: string;
      name: string;
      arrived: string;
      units: number;
    }[] = [];
    const push = (brand: string, batches: {
      arrived: string;
      name: string;
      received: number;
    }[]) => {
      batches.forEach((batch, index) => rows.push({
        id: submissionId(brand, index, batch.name),
        brand,
        name: batch.name,
        arrived: batch.arrived,
        units: batch.received
      }));
    };
    if (includes('1 People')) push('1 People', onePeopleIncomingBatches);
    if (includes('Eddy')) push('Eddy', eddyIncomingBatches);
    if (includes('Nisolo')) push('Nisolo', nisoloSubmissions?.submissions ?? []);
    return rows;
  }, [selectedOwner, nisoloSubmissions]);
  const [scopeId, setScopeId] = useState<string>(initialScopeId ?? '');
  const [scopeOpen, setScopeOpen] = useState(false);
  // Arriving from an upload's Guardrails button lands directly on that submission.
  // Submissions load asynchronously, so the request is held until it resolves.
  const pendingScopeId = useRef<string | null>(initialScopeId);
  useEffect(() => {
    if (initialScopeId) {
      pendingScopeId.current = initialScopeId;
      setScopeId(initialScopeId);
    }
  }, [initialScopeId]);
  useEffect(() => {
    const requested = pendingScopeId.current;
    if (requested) {
      if (submissions.some(submission => submission.id === requested)) {
        pendingScopeId.current = null;
        setScopeId(requested);
      }
      return;
    }
    // Fall back to the first submission in view whenever the current one disappears.
    setScopeId(current => submissions.some(submission => submission.id === current) ? current : submissions[0]?.id ?? '');
  }, [submissions]);
  const activeSubmission = submissions.find(submission => submission.id === scopeId) ?? null;

  // Every submission keeps its own rules; switching the selector swaps them out.
  const [rulesById, setRulesById] = useState<Record<string, SubmissionSettings>>({});
  // Some brands carry their own negotiated pricing limit as a starting point.
  const brandDefaults = (brand?: string, batchName?: string): SubmissionSettings => {
    const merged = {
      ...defaultSettings,
      ...(brand ? brandSettingOverrides[brand] ?? {} : {}),
      ...(batchName ? submissionSettingOverrides[batchName] ?? {} : {})
    };
    return {
      ...merged,
      indexAgreement: merged.indexPrice
    };
  };
  const scopeDefaults = brandDefaults(activeSubmission?.brand, activeSubmission?.name);
  const settings = rulesById[scopeId] ?? scopeDefaults;
  const updateSettings = (patch: Partial<SubmissionSettings>) => {
    if (!scopeId) return;
    setRulesById(current => ({
      ...current,
      [scopeId]: {
        ...(current[scopeId] ?? scopeDefaults),
        ...patch
      }
    }));
  };
  const {
    channels: activeChannels,
    indexPrice,
    indexAgreement,
    indexLocked
  } = settings;
  const [infoChannel, setInfoChannel] = useState<Channel | null>(null);
  const toggleChannel = (channelId: string) => {
    updateSettings({
      channels: activeChannels.includes(channelId) ? activeChannels.filter(id => id !== channelId) : [...activeChannels, channelId]
    });
  };

  // Locking pins the index price to the agreed percentage; the slider keeps its
  // full 0-100 range so the setting stays readable in context.
  const updateIndexAgreement = (value: number) => {
    const nextValue = Math.max(0, Math.min(100, value));
    updateSettings({
      indexAgreement: nextValue,
      indexPrice: indexLocked ? nextValue : indexPrice
    });
  };
  const setIndexAgreementLock = (locked: boolean) => {
    updateSettings({
      indexLocked: locked,
      indexPrice: locked ? indexAgreement : indexPrice
    });
  };
  const setIndexPrice = (value: number) => updateSettings({
    indexPrice: value
  });
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="controls-heading" data-id="element-416" data-mp="1t6n7rksln5hl">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-417" data-mp="15gjea694km4b">
        <div data-id="element-418" data-mp="10iho71da7mjc">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-419" data-mp="10gx11wtar5vi">
            <Settings2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-420" data-mp="3gwl3riev4zdn" />
            Controls
          </span>
          <h1 id="controls-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-421" data-mp="335yaw6vsa3ph">
            Set your recovery rules.
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-422" data-mp="38257h92ai2ur">
            Choose which submissions these rules cover, where that inventory can be sold, and the price guardrails Carli should use.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-423" data-mp="3luuzbu4i21zs">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-424" data-mp="3qx3qqm1q4vzs" />
          Changes save automatically
        </span>
      </section>

      <section className="mt-8 rounded-3xl border border-[#BB5E40]/25 bg-[#BB5E40]/[0.05] p-5 sm:p-6" aria-labelledby="scope-heading" data-id="element-425" data-mp="2ffkfq7hwjoqo">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-426" data-mp="3746prupp9k37">
          <div data-id="element-427" data-mp="323j1vhwrpjd7">
            <div className="flex items-center gap-2" data-id="element-428" data-mp="322r88cmqdvpk">
              <LayersIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-429" data-mp="2x4podphhdsd1" />
              <h2 id="scope-heading" className="text-lg font-semibold" data-id="element-430" data-mp="2s6621nyz6ek5">Applies to</h2>
            </div>
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-ink/60" data-id="element-431" data-mp="3ptn5hp9yhj47">
              {activeSubmission ? `Rules below apply to ${activeSubmission.name} only — ${activeSubmission.units.toLocaleString('en-US')} units uploaded ${activeSubmission.arrived}. Each submission keeps its own rules.` : 'Upload inventory to set rules for a specific submission.'}
            </p>
          </div>

          <div className="relative w-full lg:w-80" data-id="element-432" data-mp="cf9ycwq367og">
            <button type="button" onClick={() => setScopeOpen(open => !open)} aria-expanded={scopeOpen} aria-haspopup="listbox" disabled={submissions.length === 0} className="flex w-full items-center justify-between gap-3 rounded-xl border border-ink/15 bg-paper px-4 py-3 text-left text-sm font-semibold transition-colors hover:border-[#BB5E40]/45 disabled:opacity-50" data-id="element-433" data-mp="cguvmu3sc4q0">
              <span className="truncate" data-id="element-434" data-mp="tya6x4y3l9jx">{activeSubmission?.name ?? 'No submissions yet'}</span>
              <ChevronDownIcon className={`h-4 w-4 shrink-0 text-ink/45 transition-transform ${scopeOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-435" data-mp="yv0hjcr4vcxy" />
            </button>

            {scopeOpen && <div role="listbox" aria-label="Inventory submissions" className="absolute right-0 z-20 mt-2 w-full rounded-xl border border-ink/10 bg-paper p-1.5 shadow-lg lg:w-96" data-id="element-436" data-mp="17ondp3z96i7o">
                <span className="block px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-ink/45" data-id="element-437" data-mp="1hncm8659eqb9">Choose one submission</span>
                <div className="max-h-72 overflow-y-auto" data-id="element-438" data-mp="1mja26bcm1uni">
                  {submissions.map(submission => {
                const selected = submission.id === scopeId;
                const customized = Boolean(rulesById[submission.id]);
                return <button key={submission.id} type="button" role="option" aria-selected={selected} onClick={() => {
                  setScopeId(submission.id);
                  setScopeOpen(false);
                }} className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors ${selected ? 'bg-[#BB5E40]/10' : 'hover:bg-cream/70'}`} data-id="element-439" data-mp="dnml9fjyt3oa">
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center" data-id="element-440" data-mp="2y7k3gdp5f831">
                          {selected && <CheckIcon className="h-3.5 w-3.5 text-[#BB5E40]" aria-hidden="true" data-id="element-441" data-mp="335btf268gk4l" />}
                        </span>
                        <span className="min-w-0 flex-1" data-id="element-442" data-mp="2vo6u68m7ygo9">
                          <span className="block truncate text-sm font-medium" data-id="element-443" data-mp="2qvdpfkqr38c6">{submission.name}</span>
                          <span className="block text-xs text-ink/50" data-id="element-444" data-mp="2lw14j5jfwbc8">
                            {allBrands ? `${submission.brand} · ` : ''}{submission.arrived} · {submission.units.toLocaleString('en-US')} units
                          </span>
                        </span>
                        {customized && <span className="shrink-0 rounded-full bg-[#BB5E40]/12 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-445" data-mp="39lvhdn3rdzwh">Custom</span>}
                      </button>;
              })}
                </div>
              </div>}
          </div>
        </div>
      </section>

      <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="channels-heading" data-id="element-446" data-mp="1t9os02indemi">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" data-id="element-447" data-mp="1p8wgt3q7sznl">
          <div data-id="element-448" data-mp="1qlutcscuqvpn">
            <div className="flex items-center gap-2" data-id="element-449" data-mp="1vmsd39difxt9">
              <SlidersHorizontalIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-450" data-mp="1vma6bit82173" />
              <h2 id="channels-heading" className="text-lg font-semibold" data-id="element-451" data-mp="1goq7ktmx529d">Approved channels</h2>
            </div>
            <p className="mt-1 text-sm leading-relaxed text-ink/60" data-id="element-452" data-mp="1lmrnj5p1slgm">Select the marketplaces where this inventory is eligible to appear.</p>
          </div>
          <span className="text-sm font-medium text-ink/55" data-id="element-453" data-mp="1cog1lgz4mbui">{activeChannels.length} selected</span>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-id="element-454" data-mp="y031c7t0a6u7">
          {channels.map(channel => {
          const selected = activeChannels.includes(channel.id);
          const infoOpen = infoChannel?.id === channel.id;
          return <div key={channel.id} className="relative" data-id="element-455" data-mp="szxfj89f3ek1">
                <button type="button" onClick={() => toggleChannel(channel.id)} aria-pressed={selected} className={`flex min-h-[76px] w-full items-center gap-2.5 rounded-xl border p-3 pr-7 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20 ${selected ? 'border-[#BB5E40] bg-[#BB5E40] text-cream shadow-sm' : 'border-ink/10 bg-cream/40 hover:border-[#BB5E40]/30'}`} data-id="element-456" data-mp="szfdfv02q647">
                  <ChannelBadge channel={channel} selected={selected} data-id="element-457" data-mp="t8625hjq0uma" />
                  <span className={`min-w-0 flex-1 whitespace-normal break-words text-[13px] font-semibold leading-tight sm:text-sm ${selected ? 'text-cream' : 'text-ink'}`} data-id="element-458" data-mp="oawd4308ukyz">{channel.name}</span>
                </button>
                <button type="button" onClick={() => setInfoChannel(infoOpen ? null : channel)} aria-label={`About ${channel.name}`} aria-expanded={infoOpen} className={`absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20 ${selected ? 'text-cream/75 hover:bg-cream/15 hover:text-cream' : 'text-ink/45 hover:bg-ink/5 hover:text-[#BB5E40]'}`} data-id="element-459" data-mp="y3bv30bjj9jv">
                  <InfoIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-460" data-mp="24un55xxzrd1v" />
                </button>
                {infoOpen && <div role="dialog" aria-label={`About ${channel.name}`} className="absolute left-0 top-[calc(100%+8px)] z-20 w-64 rounded-xl border border-ink/10 bg-paper p-4 text-left shadow-lg" data-id="element-461" data-mp="1c6ra0xo2lt77">
                    <div className="flex items-start justify-between gap-3" data-id="element-462" data-mp="1m4xadau8ar9y">
                      <div data-id="element-463" data-mp="1m2ugugtvag0h">
                        <p className="text-sm font-semibold text-ink" data-id="element-464" data-mp="2sl9cfn068tat">{channel.name}</p>
                        <p className="mt-1 text-xs leading-relaxed text-ink/60" data-id="element-465" data-mp="2xk3uunu88fue">Channel description coming soon. Add the approved positioning, audience, and merchandising notes for this marketplace here.</p>
                      </div>
                      <button type="button" onClick={() => setInfoChannel(null)} className="-mr-1 -mt-1 rounded-md p-1 text-ink/45 transition-colors hover:bg-ink/5 hover:text-ink" aria-label={`Close ${channel.name} details`} data-id="element-466" data-mp="gyp2r2fdtckw">
                        <XIcon className="h-3.5 w-3.5" data-id="element-467" data-mp="346avkx2drgu" />
                      </button>
                    </div>
                  </div>}
              </div>;
        })}
        </div>
      </section>

      <section className="mt-5" data-id="element-468" data-mp="3kemh7nkkt6p1">
        <article className="rounded-3xl border border-ink/10 bg-paper p-5 sm:p-7" aria-labelledby="pricing-heading" data-id="element-469" data-mp="3kffnknfmhu6n">
          <div className="flex items-center gap-2" data-id="element-470" data-mp="1nlkp9i479pff">
            <CircleDollarSignIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-471" data-mp="1imhssm2acrj2" />
            <h2 id="pricing-heading" className="text-lg font-semibold" data-id="element-472" data-mp="3d3hum9ni4622">Pricing guardrails</h2>
          </div>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-ink/60" data-id="element-473" data-mp="3hugcves1ve7y">This setting guides how your inventory is publicly merchandised.</p>

          <div className="mt-7 space-y-7" data-id="element-474" data-mp="336mu5l7o3vl5">
            <PricingSlider label="Public pricing limit" value={indexPrice} onChange={setIndexPrice} helper="The maximum discount from MSRP at which inventory can be publicly listed." min={0} max={100} agreement={indexAgreement} onAgreementChange={updateIndexAgreement} locked={indexLocked} canUnlock={isAdmin} onLockChange={setIndexAgreementLock} data-id="element-475" data-mp="383vffuo44fqm" />
          </div>
        </article>
      </section>

      <GeographySelector brand={activeSubmission?.brand} data-id="element-476" data-mp="2bckwvinxbd8h" />

      <PromotionSettings brand={activeSubmission?.brand} submissionName={activeSubmission?.name} data-id="element-477" data-mp="2ffpsr2di0go5" />
    </div>;
}
function ChannelBadge({
  channel,
  selected
}: {
  channel: Channel;
  selected: boolean;
}) {
  return <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg p-2 ${selected ? 'bg-cream/15' : 'bg-[#BB5E40]'}`} data-id="element-478" data-mp="22vu166ynkgtk"><img src={channel.iconUrl} alt="" className={`h-full w-full object-contain ${channel.id === 'ebay' ? 'scale-125' : ''}`} data-id="element-479" data-mp="2hs1tjuk7sojx" /></span>;
}
function PricingSlider({
  label,
  value,
  onChange,
  helper,
  min,
  max,
  agreement,
  onAgreementChange,
  locked,
  canUnlock,
  onLockChange
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  helper: string;
  min: number;
  max: number;
  agreement: number;
  onAgreementChange: (value: number) => void;
  locked: boolean;
  canUnlock: boolean;
  onLockChange: (locked: boolean) => void;
}) {
  const agreementId = `${label.toLowerCase().replace(' ', '-')}-agreement`;
  const lockId = `${label.toLowerCase().replace(' ', '-')}-lock`;
  return <div data-id="element-480" data-mp="11e1vyq8udazu">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-481" data-mp="fwjki398j3fd">
        <label className="pt-1 text-sm font-semibold text-ink" data-id="element-482" data-mp="fywc784sos5m">{label}</label>
        <div className="flex items-center gap-2 self-start rounded-xl border border-ink/10 bg-cream/65 p-1.5" data-id="element-483" data-mp="kvwfxce69itk">
          <label htmlFor={agreementId} className="sr-only" data-id="element-484" data-mp="pwtz9sax4iwn">Agreed {label.toLowerCase()} discount off MSRP</label>
          <input id={agreementId} type="number" min={min} max={100} value={agreement} disabled={locked} onChange={event => onAgreementChange(Number(event.target.value))} className={`w-12 bg-transparent text-right text-sm font-semibold tabular-nums text-ink focus:outline-none ${locked ? 'cursor-not-allowed text-ink/50' : ''}`} data-id="element-485" data-mp="yvfd5a5g39r3" />
          <span className="text-xs font-medium text-ink/50" data-id="element-486" data-mp="1hnhuknpwuu2e">% off agreed</span>
          <span className="h-5 w-px bg-ink/10" aria-hidden="true" data-id="element-487" data-mp="1mm1i2sk5kdwv" />
          {canUnlock ? <>
              <input id={lockId} type="checkbox" checked={locked} onChange={event => onLockChange(event.target.checked)} className="h-4 w-4 rounded border-ink/25 accent-[#BB5E40]" data-id="element-488" data-mp="374luqipwit90" />
              <label htmlFor={lockId} className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-ink/65" data-id="element-489" data-mp="2t4b33dgrpams">
                <LockKeyholeIcon className={`h-3.5 w-3.5 ${locked ? 'text-[#BB5E40]' : 'text-ink/40'}`} aria-hidden="true" data-id="element-490" data-mp="3pt0huux3ln4j" />
                Lock
              </label>
            </> : <span className="flex items-center gap-1.5 text-xs font-semibold text-ink/55" data-id="element-491" data-mp="3gznkngs03zcn">
              <LockKeyholeIcon className={`h-3.5 w-3.5 ${locked ? 'text-[#BB5E40]' : 'text-ink/40'}`} aria-hidden="true" data-id="element-492" data-mp="3c1c6ituob2om" />
              {locked ? 'Locked' : 'Unlocked'}
            </span>}
        </div>
      </div>
      <div className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-ink/55" data-id="element-493" data-mp="8jbxdbddsr9f">
        <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-494" data-mp="q9yjhfvcmwyl" />
        <p data-id="element-495" data-mp="l8945o13fx4p">{helper}</p>
      </div>
      <div className="mt-3 flex items-baseline justify-between gap-3" data-id="element-496" data-mp="gahel0nx8n3o">
        <span className="text-xs font-medium uppercase tracking-wider text-ink/45" data-id="element-497" data-mp="gapnyiku5aa4">Current setting</span>
        <span className="font-display text-2xl font-semibold tabular-nums text-[#BB5E40]" data-id="element-498" data-mp="bdh8ipr2nkck">{value}% <span className="font-sans text-xs font-medium text-ink/45" data-id="element-499" data-mp="18yd67qymvotl">OFF MSRP</span></span>
      </div>
      <input type="range" min={min} max={max} step={1} value={value} disabled={locked} onChange={event => onChange(Number(event.target.value))} className={`mt-3 w-full accent-[#BB5E40] ${locked ? 'cursor-not-allowed opacity-60' : ''}`} aria-label={`${label} percentage off MSRP`} data-id="element-500" data-mp="140trkpy0xa14" />
      <div className="mt-1 flex justify-between text-xs text-ink/45" data-id="element-501" data-mp="1mxv18o2ivybe">
        <span data-id="element-502" data-mp="1v7tp3af65w77">{min}%</span>
        <span data-id="element-503" data-mp="1v8lmmqsbu404">{max}%</span>
      </div>
      {locked && <p className="mt-2 text-xs font-medium text-[#94452E]" data-id="element-504" data-mp="1z95izs52t3l7">
          Locked at the agreed {agreement}% off MSRP. {canUnlock ? 'Unlock to adjust.' : 'Contact ManyCo to change your agreement.'}
        </p>}
    </div>;
}